export interface PDFPageSearchHit {
  docId: string;
  attachmentBlockId: string;
  pageIndex: number;
  source?: string | null;
  coverageStatus?: string | null;
  snippet: string;
  score: number;
  rects: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
}

export type PDFFindSourceMode =
  | 'idle'
  | 'remote'
  | 'local'
  | 'hybrid'
  | 'local-only';

export type PDFFindNotice =
  | 'local-only'
  | 'partial'
  | 'cap-reached'
  | 'ocr-unavailable-offline'
  | 'remote-unavailable'
  | null;

export interface PDFFindCoverage {
  totalPageCount: number;
  searchedPageIndexes: number[];
  textLayerPageIndexes: number[];
  emptyTextLayerPageIndexes: number[];
  scanning: boolean;
  complete: boolean;
  capReached: boolean;
  incompleteReason?: 'cap-reached' | 'aborted' | 'pdf-error';
}

export type PDFFindStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export interface PDFFindNoticeLabelMessages {
  capReached(input: { searched: string; total: string }): string;
  localOnly(): string;
  ocrUnavailableOffline(): string;
  partial(input: { searched: string; total: string }): string;
  remoteUnavailable(): string;
}

export interface PDFFindState {
  open: boolean;
  query: string;
  status: PDFFindStatus;
  hits: PDFPageSearchHit[];
  activeIndex: number;
  error: string | null;
  sourceMode: PDFFindSourceMode;
  coverage: PDFFindCoverage | null;
  notice: PDFFindNotice;
  /**
   * Monotonic counter incremented only on explicit user scroll intent
   * (next/previous/activate, or first result of a user-typed query). It is
   * NOT incremented when a background search merely replaces the hit list.
   * Consumers scroll to the active hit when this nonce changes — decoupling
   * "scroll to hit" from "hit list updated" so a navigation-opened viewer
   * (cmdk hit) is not yanked away from its target page by the live re-search.
   */
  scrollNonce: number;
  /**
   * True after the user typed a query (find bar): the next incoming results
   * should auto-scroll to the first match. Reset to false on programmatic
   * `open` (cmdk / Cmd+F) so navigation-driven opens do not auto-jump.
   */
  autoScrollOnResults: boolean;
}

export const INITIAL_PDF_FIND_STATE: PDFFindState = {
  open: false,
  query: '',
  status: 'idle',
  hits: [],
  activeIndex: 0,
  error: null,
  sourceMode: 'idle',
  coverage: null,
  notice: null,
  scrollNonce: 0,
  autoScrollOnResults: false,
};

export type PDFFindAction =
  | { type: 'open'; query?: string }
  | { type: 'close' }
  | { type: 'query'; query: string }
  | { type: 'loading' }
  | {
      type: 'results';
      hits: PDFPageSearchHit[];
      coverage?: PDFFindCoverage | null;
      fallbackActiveHit?: PDFPageSearchHit;
      notice?: PDFFindNotice;
      preserveActive?: boolean;
      sourceMode?: PDFFindSourceMode;
    }
  | { type: 'error'; error: string }
  | { type: 'next' }
  | { type: 'previous' }
  | { type: 'activate'; index: number };

export function pdfFindReducer(
  state: PDFFindState,
  action: PDFFindAction
): PDFFindState {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        open: true,
        query: action.query ?? state.query,
        // Programmatic open (cmdk hit / Cmd+F) must not auto-jump on the
        // results that follow — positioning is owned by the navigation
        // controller, not the find bar.
        autoScrollOnResults: false,
      };
    case 'close':
      return INITIAL_PDF_FIND_STATE;
    case 'query':
      return {
        ...state,
        open: true,
        query: action.query,
        status: action.query.trim() ? state.status : 'idle',
        hits: action.query.trim() ? state.hits : [],
        activeIndex: 0,
        error: null,
        coverage: action.query.trim() ? state.coverage : null,
        notice: null,
        sourceMode: action.query.trim() ? state.sourceMode : 'idle',
        // User typed in the find bar → scroll to the first match once it
        // arrives.
        autoScrollOnResults: !!action.query.trim(),
      };
    case 'loading':
      return {
        ...state,
        status: 'loading',
        error: null,
        notice: null,
      };
    case 'results':
      const activeKey =
        action.preserveActive && state.hits[state.activeIndex]
          ? createPdfFindHitKey(state.hits[state.activeIndex])
          : null;
      const activeIndex = activeKey
        ? action.hits.findIndex(hit => createPdfFindHitKey(hit) === activeKey)
        : -1;
      const hits =
        activeIndex >= 0 || !action.fallbackActiveHit
          ? action.hits
          : [...action.hits, action.fallbackActiveHit].sort(
              (a, b) => a.pageIndex - b.pageIndex || b.score - a.score
            );
      const fallbackActiveIndex =
        activeIndex >= 0 || !action.fallbackActiveHit
          ? -1
          : hits.findIndex(
              hit =>
                createPdfFindHitKey(hit) ===
                createPdfFindHitKey(
                  action.fallbackActiveHit as PDFPageSearchHit
                )
            );
      const coverage = action.coverage ?? null;
      return {
        ...state,
        status:
          hits.length > 0 ? 'ready' : coverage?.scanning ? 'loading' : 'empty',
        hits,
        activeIndex:
          activeIndex >= 0
            ? activeIndex
            : fallbackActiveIndex >= 0
              ? fallbackActiveIndex
              : 0,
        error: null,
        sourceMode: action.sourceMode ?? state.sourceMode,
        coverage,
        notice: action.notice ?? null,
        scrollNonce:
          state.autoScrollOnResults && action.hits.length > 0
            ? state.scrollNonce + 1
            : state.scrollNonce,
        autoScrollOnResults: false,
      };
    case 'error':
      return {
        ...state,
        status: 'error',
        hits: [],
        activeIndex: 0,
        error: action.error,
        sourceMode: 'idle',
        coverage: null,
        notice: null,
        autoScrollOnResults: false,
      };
    case 'next':
      return {
        ...state,
        activeIndex: getNextIndex(state.activeIndex, state.hits.length, 1),
        scrollNonce: state.scrollNonce + 1,
      };
    case 'previous':
      return {
        ...state,
        activeIndex: getNextIndex(state.activeIndex, state.hits.length, -1),
        scrollNonce: state.scrollNonce + 1,
      };
    case 'activate':
      return {
        ...state,
        activeIndex: clampIndex(action.index, state.hits.length),
        scrollNonce: state.scrollNonce + 1,
      };
    default:
      return state;
  }
}

export function createPdfFindHitKey(hit: PDFPageSearchHit): string {
  const firstRect = hit.rects[0];
  const rectKey = firstRect
    ? [
        firstRect.x.toFixed(4),
        firstRect.y.toFixed(4),
        firstRect.width.toFixed(4),
        firstRect.height.toFixed(4),
      ].join(':')
    : 'no-rect';
  return [
    hit.attachmentBlockId,
    hit.pageIndex,
    hit.source ?? 'unknown',
    normalizeFindKeyText(hit.snippet),
    rectKey,
  ].join('|');
}

export function getActivePdfFindHit(
  state: PDFFindState
): PDFPageSearchHit | null {
  return state.hits[state.activeIndex] ?? null;
}

export function formatPdfFindCount(state: PDFFindState): string {
  if (state.status === 'loading') return '...';
  if (state.status === 'error') return '!';
  if (state.hits.length === 0) return '0/0';
  return `${state.activeIndex + 1}/${state.hits.length}`;
}

export function formatPdfFindNoticeLabel(
  state: Pick<PDFFindState, 'coverage' | 'notice'>,
  messages: PDFFindNoticeLabelMessages
): string | null {
  const { coverage } = state;

  switch (state.notice) {
    case 'cap-reached':
      return messages.capReached({
        searched: String(coverage?.searchedPageIndexes.length ?? 0),
        total: String(coverage?.totalPageCount ?? 0),
      });
    case 'local-only':
      return messages.localOnly();
    case 'ocr-unavailable-offline':
      return messages.ocrUnavailableOffline();
    case 'partial':
      return coverage
        ? messages.partial({
            searched: String(coverage.searchedPageIndexes.length),
            total: String(coverage.totalPageCount),
          })
        : null;
    case 'remote-unavailable':
      return messages.remoteUnavailable();
    default:
      return null;
  }
}

function getNextIndex(current: number, total: number, direction: 1 | -1) {
  if (total <= 0) return 0;
  return (current + direction + total) % total;
}

function clampIndex(index: number, total: number) {
  if (total <= 0) return 0;
  if (!Number.isInteger(index)) return 0;
  return Math.min(Math.max(index, 0), total - 1);
}

function normalizeFindKeyText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase()
    .replace(/\s+/g, '');
}
